/**
 * AssignDriverModal — admin control-room recovery modal.
 *
 * Opens from the Assign / Reassign button on a stuck ride. Shows EVERY
 * driver in the fleet (not just online ones) with per-driver eligibility
 * fetched live from `/admin/driver-eligibility/{driver_id}`.
 *
 * Why this exists:
 *   The previous AssignDropdown was a small popover that only listed
 *   `is_online` drivers and rendered a disabled "No drivers online"
 *   span when none were — exactly when an admin needs to recover. The
 *   user reported "Assign opens a blank table" which is precisely that
 *   dead-end. This component replaces it with a full modal that:
 *     • Lists every driver with status pills (online / offline /
 *       suspended / busy / no_location).
 *     • Hits /admin/driver-eligibility/{id} for the precise reason a
 *       driver is excluded (e.g. `pending_offer`, `on_trip`,
 *       `no_location`, `suspended`, `offline`).
 *     • Surfaces a one-click "Force-online + assign" path for drivers
 *       who are offline but otherwise fine (admin uses
 *       /admin/drivers/{id}/online → then /admin/assign).
 *     • Shows visible error toasts on every backend failure (the old
 *       version surfaced nothing on error).
 */
import React, { useEffect, useState } from "react";
import { X, Search, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const REASON_LABELS = {
  eligible: "Eligible",
  offline: "Offline",
  no_location: "No GPS location",
  suspended: "Suspended",
  on_trip: "Mid-trip",
  pending_offer: "Has another offer",
  declined_by: "Declined this ride",
};

const REASON_COLORS = {
  eligible: { bg: "bg-[#10B981]", text: "text-white" },
  offline: { bg: "bg-[#9CA3AF]", text: "text-white" },
  no_location: { bg: "bg-[#F59E0B]", text: "text-black" },
  suspended: { bg: "bg-[#EF4444]", text: "text-white" },
  on_trip: { bg: "bg-[#0066FF]", text: "text-white" },
  pending_offer: { bg: "bg-[#FFC107]", text: "text-black" },
  declined_by: { bg: "bg-[#9333EA]", text: "text-white" },
};

export const AssignDriverModal = ({ ride, drivers, onClose, onAssign }) => {
  const [eligibility, setEligibility] = useState({}); // {driver_id: {eligible, reason, evidence}}
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [assigning, setAssigning] = useState(null);

  // Fetch eligibility for every driver in parallel when the modal opens.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const results = await Promise.all(
        drivers.map((d) =>
          api.get(`/admin/driver-eligibility/${d.id}?ride_id=${ride.id}`)
            .then((res) => [d.id, res.data])
            .catch((e) => [d.id, { eligible: false, reason: "lookup_failed", evidence: { error: formatApiError(e) } }]),
        ),
      );
      if (cancelled) return;
      const map = {};
      results.forEach(([id, data]) => { map[id] = data; });
      setEligibility(map);
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [ride.id, drivers]);

  const handleAssign = async (driver) => {
    setAssigning(driver.id);
    try {
      // If the driver isn't online yet, bring them online first so the
      // backend's `/admin/assign` eligibility check doesn't reject them.
      if (!driver.is_online) {
        await api.post(`/admin/drivers/${driver.id}/online`);
      }
      await onAssign(ride.id, driver.id, driver.name);
      toast.success(`Assigned ride to ${driver.name}`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to assign driver");
      setAssigning(null);
    }
  };

  const filtered = drivers.filter((d) => {
    const q = filter.toLowerCase().trim();
    if (!q) return true;
    return (d.name || "").toLowerCase().includes(q)
        || (d.email || "").toLowerCase().includes(q)
        || (d.vehicle_plate || "").toLowerCase().includes(q);
  });
  // Sort: eligible first, then online, then alphabetical.
  filtered.sort((a, b) => {
    const ea = eligibility[a.id]?.eligible ? 0 : 1;
    const eb = eligibility[b.id]?.eligible ? 0 : 1;
    if (ea !== eb) return ea - eb;
    if (a.is_online !== b.is_online) return a.is_online ? -1 : 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  const isReassign = !!ride.driver_id || !!ride.offered_to;
  const eligibleCount = Object.values(eligibility).filter((e) => e?.eligible).length;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="assign-driver-modal"
    >
      <div className="bg-white border border-border w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="label-mini text-muted-foreground">{isReassign ? "REASSIGN RIDE" : "ASSIGN RIDE"}</div>
            <h2 className="font-heading text-xl font-black mt-1">
              Pick a driver for {ride.passenger_name}
            </h2>
            <div className="text-xs text-muted-foreground mt-1 font-mono-tab">
              ride {ride.id.substring(0, 8)} · {ride.status} · £{ride.fare || ride.estimated_fare}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary transition-colors"
            data-testid="assign-modal-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border bg-secondary/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name, email, plate…"
              className="w-full pl-10 pr-3 py-2 border border-border bg-white text-sm focus:outline-none focus:border-[#0A0A0A]"
              data-testid="assign-modal-filter"
              autoFocus
            />
          </div>
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#10B981]" />{loading ? "…" : eligibleCount} eligible</span>
            <span>·</span>
            <span>{drivers.length} total in fleet</span>
            <span>·</span>
            <span>{drivers.filter((d) => d.is_online).length} online</span>
          </div>
        </div>

        {/* Driver list */}
        <div className="flex-1 overflow-y-auto" data-testid="assign-modal-driver-list">
          {loading ? (
            <div className="px-5 py-8 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading eligibility for {drivers.length} drivers…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-8 text-center text-muted-foreground text-sm" data-testid="assign-modal-empty-filter">
              {drivers.length === 0
                ? "No drivers in the fleet."
                : "No drivers match your filter."}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((d) => {
                const elig = eligibility[d.id] || { eligible: false, reason: "loading" };
                const reasonLabel = REASON_LABELS[elig.reason] || elig.reason || "—";
                const colorSet = REASON_COLORS[elig.reason] || { bg: "bg-secondary", text: "text-foreground" };
                const isCurrent = ride.offered_to === d.id || ride.driver_id === d.id;
                const isAssigning = assigning === d.id;
                return (
                  <div
                    key={d.id}
                    className={`px-5 py-3 hover:bg-secondary/30 transition-colors flex items-center gap-3 ${
                      isCurrent ? "bg-[#10B981]/10" : ""
                    }`}
                    data-testid={`assign-modal-driver-${d.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{d.name || d.email}</span>
                        {isCurrent && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#10B981] border border-[#10B981] px-1.5 py-0.5">
                            Current
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${colorSet.bg} ${colorSet.text}`}
                          data-testid={`assign-modal-reason-${d.id}`}
                        >
                          {reasonLabel}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-mono-tab truncate">
                        {d.vehicle_plate || "—"} · {d.vehicle_make || "—"} · {d.is_online ? "Online" : "Offline"}
                        {elig.evidence?.distance_km != null && ` · ${elig.evidence.distance_km} km away`}
                      </div>
                      {elig.evidence?.error && (
                        <div className="text-[11px] text-[#EF4444] mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {elig.evidence.error}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleAssign(d)}
                      disabled={isCurrent || isAssigning}
                      className={`text-xs px-3 py-2 font-bold uppercase tracking-wider transition-colors flex-shrink-0 ${
                        isCurrent
                          ? "bg-secondary text-muted-foreground cursor-not-allowed"
                          : elig.eligible
                          ? "bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
                          : "bg-white border border-[#FFC107] text-[#92400E] hover:bg-[#FFFBEB]"
                      }`}
                      data-testid={`assign-modal-pick-${d.id}`}
                    >
                      {isAssigning ? (
                        <Loader2 className="w-3 h-3 animate-spin inline" />
                      ) : isCurrent ? (
                        "Current"
                      ) : elig.eligible ? (
                        "Assign"
                      ) : (
                        "Force-assign"
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 border-t border-border bg-secondary/20 text-[11px] text-muted-foreground">
          <strong>Force-assign</strong>: brings an offline driver online first, then assigns. The driver's app will receive the offer on their next poll.
        </div>
      </div>
    </div>
  );
};

export default AssignDriverModal;
